/**
 * Service de tarification centralisé pour OMAD SERVICES
 * 
 * Ce service unifie la logique de calcul des tarifs en se basant sur :
 * - La grille tarifaire du livret HTML
 * - Les paramètres du contrat client
 * - Les spécificités des prestations
 */

// Tarifs de base selon le livret HTML
const TARIFS_BASE = {
  STANDARD: 24.41, // €/h (CP inclus)
  EXONERE_70: 17.17 // €/h (CP inclus) pour bénéficiaires 70+
};

// Tarifs de référence par type de prestation (Nouveau Système Multi-Contrats)
const TARIFS_REFERENCE = {
  assistance_vie: {
    label: "Assistance de vie",
    standard: 24.41,
    plus70: 17.17
  },
  menage: {
    label: "Ménage / Repassage",
    standard: 30.08,
    plus70: 25.81
  },
  accompagnement: {
    label: "Accompagnement",
    standard: 35.76,
    plus70: 30.38
  }
};

// Frais mandat OMAD selon volume hebdomadaire
const FRAIS_MANDAT = {
  '≤10h/sem': 6.00,
  '11–25h/sem': 5.40,
  '26–40h/sem': 4.20,
  '>40h/sem': 3.00
};

// Majorations selon type
const MAJORATIONS = {
  none: { label: 'Aucune', factor: 1.0 },
  weekend: { label: 'Dimanche', factor: 1.25 }, // +25%
  holiday: { label: 'Jour férié', factor: 1.50 }, // +50%
  may1: { label: '1er mai', factor: 2.0 } // +100%
};

// Types de prestations avec équivalence horaire
// Types de prestations avec équivalence horaire
const TYPES_PRESTATIONS = {
  assistance_vie: { label: 'Assistance de vie', unit: 'heure', heures: 1, nuit: false },
  menage: { label: 'Ménage / Repassage', unit: 'heure', heures: 1, nuit: false },
  accompagnement: { label: 'Accompagnement', unit: 'heure', heures: 1, nuit: false },

  // Legacy / Compatibilité
  aide_domicile: { label: 'Aide à domicile', unit: 'heure', heures: 1, nuit: false },
  aide_personne: { label: 'Aide à la personne', unit: 'heure', heures: 1, nuit: false },
  nuit_passive: { label: 'Garde nuit passive', unit: 'nuit', heures: 4, nuit: true },
  nuit_active: { label: 'Garde nuit active', unit: 'nuit', heures: 10, nuit: true }
};

/**
 * Calcule les frais de déplacement selon la distance et le collaborateur
 * @param {number} distanceAllerKm - Distance aller en km
 * @param {Object} collaborateur - Informations du collaborateur (optionnel)
 * @returns {number} Frais de déplacement en €
 */
export const calculerFraisDeplacement = (distanceAllerKm, collaborateur = null) => {
  // Forfait de base pour 20 km aller-retour (10 km aller + 10 km retour)
  const distanceAR = distanceAllerKm * 2; // Distance aller-retour

  // Si collaborateur auto-entrepreneur, utiliser son tarif de déplacement personnalisé
  if (collaborateur && collaborateur.isAutoEntrepreneur && collaborateur.deplacementRate) {
    // Tarif personnalisé pour auto-entrepreneur
    const forfaitBase = collaborateur.deplacementRate.base || 2.50;
    const tarifKmSupplementaire = collaborateur.deplacementRate.perKm || 0.40;
    const distanceIncluse = collaborateur.deplacementRate.includedDistance || 20;

    if (distanceAR <= distanceIncluse) return forfaitBase;
    return forfaitBase + tarifKmSupplementaire * (distanceAR - distanceIncluse);
  }

  // Tarif standard OMAD
  if (distanceAR <= 20) return 2.50; // Forfait base pour 20 km AR
  return 2.50 + 0.40 * (distanceAR - 20); // 0,40€/km au-delà de 20 km AR
};

/**
 * Détermine les frais mandat OMAD selon le volume horaire hebdomadaire
 * @param {number} volumeHebdo - Volume horaire hebdomadaire
 * @returns {number} Frais mandat en €/h
 */
export const getFraisMandat = (volumeHebdo) => {
  if (volumeHebdo <= 10) return FRAIS_MANDAT['≤10h/sem'];
  if (volumeHebdo <= 25) return FRAIS_MANDAT['11–25h/sem'];
  if (volumeHebdo <= 40) return FRAIS_MANDAT['26–40h/sem'];
  return FRAIS_MANDAT['>40h/sem'];
};

/**
 * Obtient le tarif de base selon le type de bénéficiaire
 * @param {boolean} estBenef70 - Si bénéficiaire 70+
 * @returns {number} Tarif de base en €/h
 */
export const getTarifBase = (estBenef70) => {
  return estBenef70 ? TARIFS_BASE.EXONERE_70 : TARIFS_BASE.STANDARD;
};

/**
 * Applique une majoration au tarif de base
 * @param {number} tarifBase - Tarif de base
 * @param {string} typeMajoration - Type de majoration
 * @returns {number} Tarif majoré
 */
export const appliquerMajoration = (tarifBase, typeMajoration) => {
  const majoration = MAJORATIONS[typeMajoration] || MAJORATIONS.none;
  return tarifBase * majoration.factor;
};

/**
 * Calcule le coût total d'une ligne de prestation
 * @param {Object} ligne - Ligne de prestation
 * @param {Object} contrat - Paramètres du contrat client
 * @param {Object} collaborateur - Informations du collaborateur (optionnel)
 * @returns {Object} Détails du calcul
 */
export const calculerLignePrestation = (ligne, contrat, collaborateur = null) => {
  const {
    typePrestation,
    quantite,
    majoration = 'none',
    distanceAllerKm = 0,
    nbInterventions = 0,
    employeeId = null
  } = ligne;

  const {
    baseRateType = 'standard',
    weeklyHours = 0
  } = contrat;

  // Vérifier si bénéficiaire 70+
  const estBenef70 = baseRateType === '70plus';

  // Obtenir le type de prestation
  const type = TYPES_PRESTATIONS[typePrestation];
  if (!type) {
    throw new Error(`Type de prestation inconnu: ${typePrestation}`);
  }

  // Calculer les heures équivalentes
  const heuresTotal = type.heures * quantite;

  // Tarif de base - si collaborateur auto-entrepreneur, utiliser son tarif horaire
  let tarifBase;
  let tarifPersonnalise = false;

  if (collaborateur && collaborateur.isAutoEntrepreneur && collaborateur.hourlyRate) {
    tarifBase = collaborateur.hourlyRate;
    tarifPersonnalise = true;
  } else {
    // NOUVEAU: Vérifier si le type de prestation a un tarif spécifique dans TARIFS_REFERENCE
    const tarifRef = TARIFS_REFERENCE[typePrestation];
    if (tarifRef) {
      tarifBase = estBenef70 ? tarifRef.plus70 : tarifRef.standard;
    } else {
      // Fallback sur les anciens tarifs de base génériques
      tarifBase = getTarifBase(estBenef70);
    }
  }

  // Appliquer majoration (uniquement sur la part CESU)
  const tarifCesuUnitaire = appliquerMajoration(tarifBase, majoration);
  const totalCesu = tarifCesuUnitaire * heuresTotal;

  // Frais mandat OMAD (ne s'applique pas aux auto-entrepreneurs)
  let fraisMandatUnitaire = 0;
  let totalMandat = 0;

  if (!collaborateur || !collaborateur.isAutoEntrepreneur) {
    fraisMandatUnitaire = getFraisMandat(weeklyHours);
    totalMandat = fraisMandatUnitaire * heuresTotal;
  }

  // Frais de déplacement (uniquement si au moins 1 intervention)
  const fraisDeplacementUnitaire = nbInterventions > 0 ? calculerFraisDeplacement(distanceAllerKm, collaborateur) : 0;
  const totalDeplacement = fraisDeplacementUnitaire * nbInterventions;

  // Total ligne
  const totalLigne = totalCesu + totalMandat + totalDeplacement;

  return {
    typePrestation: type.label,
    quantite,
    unite: type.unit,
    heuresTotal,
    tarifCesuUnitaire,
    totalCesu,
    fraisMandatUnitaire,
    totalMandat,
    fraisDeplacementUnitaire,
    totalDeplacement,
    totalLigne,
    estNuit: type.nuit,
    majorationAppliquee: MAJORATIONS[majoration]?.label || 'Aucune',
    tarifPersonnalise,
    collaborateurNom: collaborateur ? `${collaborateur.firstName} ${collaborateur.lastName}` : null,
    collaborateurCesu: collaborateur?.professionalInfo?.cesuNumber || null,
    collaborateurNan: collaborateur?.professionalInfo?.nanCrcesu || null,
    employeeId: employeeId
  };
};

/**
 * Calcule le coût total d'une ligne de prestation avec gestion des collaborateurs
 * @param {Object} ligne - Ligne de prestation
 * @param {Object} contrat - Paramètres du contrat client
 * @param {Array} collaborateurs - Liste des collaborateurs disponibles
 * @returns {Object} Détails du calcul
 */
export const calculerLignePrestationAvecCollaborateur = (ligne, contrat, collaborateurs = []) => {
  const collaborateur = ligne.employeeId
    ? collaborateurs.find(c => c.id === ligne.employeeId)
    : null;

  return calculerLignePrestation(ligne, contrat, collaborateur);
};

/**
 * Génère un devis complet basé sur un contrat client
 * @param {Object} contrat - Contrat client avec paramètres tarifaires
 * @param {Array} lignesPrestations - Liste des prestations
 * @param {Object} client - Informations du client
 * @param {Array} collaborateurs - Liste des collaborateurs disponibles
 * @returns {Object} Devis complet avec calculs détaillés
 */
export const genererDevis = (contrat, lignesPrestations, client, collaborateurs = []) => {
  if (!contrat || !lignesPrestations || lignesPrestations.length === 0) {
    throw new Error('Contrat et lignes de prestations requis');
  }

  // S'assurer que le type de tarif est défini
  const baseRateType = contrat.baseRateType || 'standard';
  const contractType = contrat.contractType || 'mandat';
  const weeklyHours = contrat.weeklyHours || 10;

  console.log('Génération devis avec paramètres:', {
    baseRateType,
    contractType,
    weeklyHours
  });

  // Calculer chaque ligne avec les collaborateurs
  const lignesCalculees = lignesPrestations.map(ligne =>
    calculerLignePrestationAvecCollaborateur(ligne, contrat, collaborateurs)
  );

  // Totaux généraux
  const totaux = lignesCalculees.reduce((acc, ligne) => ({
    totalCesu: acc.totalCesu + ligne.totalCesu,
    totalMandat: acc.totalMandat + ligne.totalMandat,
    totalDeplacement: acc.totalDeplacement + ligne.totalDeplacement,
    totalHeures: acc.totalHeures + ligne.heuresTotal
  }), { totalCesu: 0, totalMandat: 0, totalDeplacement: 0, totalHeures: 0 });

  const totalTTC = totaux.totalCesu + totaux.totalMandat + totaux.totalDeplacement;

  // Détails des collaborateurs utilisés
  const collaborateursUtilises = [];
  lignesCalculees.forEach((ligne, index) => {
    if (ligne.employeeId) {
      const collaborateur = collaborateurs.find(c => c.id === ligne.employeeId);
      if (collaborateur && !collaborateursUtilises.some(c => c.id === collaborateur.id)) {
        collaborateursUtilises.push({
          id: collaborateur.id,
          nom: `${collaborateur.firstName} ${collaborateur.lastName}`,
          cesuNumber: collaborateur.professionalInfo?.cesuNumber || null,
          nanCrcesu: collaborateur.professionalInfo?.nanCrcesu || null,
          isAutoEntrepreneur: collaborateur.isAutoEntrepreneur || false,
          tarifHoraire: collaborateur.hourlyRate || null,
          tarifDeplacement: collaborateur.deplacementRate || null
        });
      }
    }
  });

  // Détails des tarifs appliqués
  const tarifsAppliques = {
    tarifBase: getTarifBase(baseRateType === '70plus'),
    tarif70Plus: baseRateType === '70plus',
    fraisMandat: getFraisMandat(weeklyHours),
    majorations: MAJORATIONS
  };

  return {
    // Informations client détaillées
    client: {
      id: client.id,
      nom: `${client.firstName || ''} ${client.lastName || ''}`.trim(),
      email: client.email || '',
      telephone: client.phone || client.phoneNumber || '',
      adresse: client.address || '',
      type: client.type || 'client'
    },

    // Paramètres du contrat - GARANTIR la cohérence
    contrat: {
      type: contractType,
      volumeHebdo: weeklyHours,
      baseRateType: baseRateType, // EXPLICITEMENT défini
      tarifBase: getTarifBase(baseRateType === '70plus'),
      fraisMandat: getFraisMandat(weeklyHours),
      majorationsApplicables: contrat.majorations || {}
    },

    // Lignes de prestations détaillées
    lignes: lignesCalculees.map((ligne, index) => ({
      ...ligne,
      ligneOriginale: lignesPrestations[index],
      collaborateur: ligne.employeeId ? collaborateurs.find(c => c.id === ligne.employeeId) : null
    })),

    // Totaux détaillés
    totaux: {
      ...totaux,
      totalTTC,
      // Détail par type de coût
      detailCouts: {
        salaireCharges: totaux.totalCesu,
        fraisMandat: totaux.totalMandat,
        fraisDeplacement: totaux.totalDeplacement
      },
      // Détail par collaborateur
      parCollaborateur: collaborateursUtilises.map(collab => {
        const lignesCollab = lignesCalculees.filter(l => l.employeeId === collab.id);

        // Grouper les heures par taux horaire
        const heuresParTaux = {};
        lignesCollab.forEach(ligne => {
          const key = `${ligne.tarifCesuUnitaire}_${ligne.majorationAppliquee}`;
          if (!heuresParTaux[key]) {
            heuresParTaux[key] = {
              tauxHoraire: ligne.tarifCesuUnitaire,
              majoration: ligne.majorationAppliquee,
              heures: 0,
              montantCesu: 0
            };
          }
          heuresParTaux[key].heures += ligne.heuresTotal;
          heuresParTaux[key].montantCesu += ligne.totalCesu;
        });

        return {
          collaborateur: collab,
          totalCesu: lignesCollab.reduce((sum, l) => sum + l.totalCesu, 0),
          totalMandat: lignesCollab.reduce((sum, l) => sum + l.totalMandat, 0),
          totalDeplacement: lignesCollab.reduce((sum, l) => sum + l.totalDeplacement, 0),
          totalHeures: lignesCollab.reduce((sum, l) => sum + l.heuresTotal, 0),
          detailHeuresParTaux: Object.values(heuresParTaux)
        };
      })
    },

    // Informations sur les collaborateurs
    collaborateurs: collaborateursUtilises,

    // Paramètres tarifaires appliqués
    tarifs: tarifsAppliques,

    // Métadonnées
    dateGeneration: new Date().toISOString(),
    nombreLignes: lignesPrestations.length,
    nombreCollaborateurs: collaborateursUtilises.length,

    // Mentions légales et informations
    mentions: {
      creditImpôt: "Le crédit d'impôt de 50% s'applique uniquement sur la part CESU",
      deplacements: "Les frais de déplacement ne sont pas éligibles au crédit d'impôt",
      majorations: "Les majorations s'appliquent uniquement sur la part CESU",
      autoEntrepreneurs: "Les auto-entrepreneurs appliquent leurs tarifs personnalisés",
      validite: "Devis valable 30 jours à compter de la date d'émission"
    },

    // Configuration du calcul - GARANTIR la cohérence du type de tarif
    configuration: {
      baseRateType: baseRateType, // DUPLICATA pour garantir la cohérence
      versionCalcul: '2.0',
      avecCollaborateurs: true,
      tarifsPersonnalises: collaborateursUtilises.some(c => c.isAutoEntrepreneur),
      dateGeneration: new Date().toISOString()
    }
  };
};

/**
 * Valide la cohérence des paramètres tarifaires
 * @param {Object} contrat - Contrat à valider
 * @returns {Object} Résultat de validation
 */
export const validerContrat = (contrat) => {
  const erreurs = [];

  if (!contrat.contractType) {
    erreurs.push('Type de contrat requis');
  }

  if (contrat.weeklyHours < 0) {
    erreurs.push('Volume horaire hebdomadaire invalide');
  }

  if (!contrat.baseRateType || !['standard', '70plus'].includes(contrat.baseRateType)) {
    erreurs.push('Type de tarif de base invalide');
  }

  return {
    valide: erreurs.length === 0,
    erreurs
  };
};

/**
 * Formate un montant en euros
 * @param {number} montant - Montant à formater
 * @returns {string} Montant formaté
 */
export const formaterEuro = (montant) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(montant || 0);
};

/**
 * Exporte les constantes pour utilisation externe
 */
export {
  TARIFS_BASE,
  FRAIS_MANDAT,
  MAJORATIONS,
  TYPES_PRESTATIONS,
  TARIFS_REFERENCE
};
